import { Checkbox, Input } from 'components/ui';
import type { DashboardEditField, DashboardFieldOption } from '../dashboardEditFields.types';
import DashboardColorListField from './DashboardColorListField';
import { DashboardChipList, DashboardEmptyHint } from './dashboardControls.styles';
import DashboardEnumSelect from './DashboardEnumSelect';
import DashboardFiltersField from './DashboardFiltersField';
import DashboardNumberField from './DashboardNumberField';
import DashboardOrderedListField from './DashboardOrderedListField';
import DashboardSortKeysField from './DashboardSortKeysField';
import DashboardStringListField from './DashboardStringListField';

interface DashboardFieldControlProps {
  disabled?: boolean;
  field: DashboardEditField;
  testId?: string;
}

// Tri-state rather than a checkbox: accent_bar / show_legend / show_trend_icon are the one styling
// rule the backend errors on instead of coercing, so "unset" has to stay distinguishable from false.
const BOOLEAN_OPTIONS: DashboardFieldOption[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

const DashboardFieldControl = ({ disabled = false, field, testId }: DashboardFieldControlProps) => {
  switch (field.kind) {
    case 'text':
    case 'textarea':
      return (
        <Input
          disabled={disabled}
          multiline={field.kind === 'textarea'}
          maxRows={field.kind === 'textarea' ? 8 : undefined}
          minRows={field.kind === 'textarea' ? 3 : undefined}
          onChange={event => field.onChange(event.target.value)}
          size="small"
          value={field.value}
          visualVariant="compactOutlined"
          // On the html input rather than the root: TextField spreads unknown props onto its
          // wrapper div, where a test id is useless for typing into.
          slotProps={{ htmlInput: { 'data-testid': testId } }}
        />
      );
    case 'number':
      return (
        <DashboardNumberField
          disabled={disabled}
          isRequired={field.isRequired}
          max={field.max}
          min={field.min}
          onChange={field.onChange}
          testId={testId}
          value={field.value}
        />
      );
    case 'select':
      return (
        <DashboardEnumSelect
          disabled={disabled}
          isRequired={field.isRequired}
          onChange={field.onChange}
          options={field.options}
          searchable={field.searchable}
          testId={testId}
          value={field.value}
        />
      );
    case 'boolean':
      return (
        <DashboardEnumSelect
          disabled={disabled}
          onChange={next => field.onChange(next === undefined ? undefined : next === 'true')}
          options={BOOLEAN_OPTIONS}
          testId={testId}
          value={field.value === undefined ? undefined : String(field.value)}
        />
      );
    case 'orderedList':
      return (
        <DashboardOrderedListField
          addLabel={field.addLabel}
          disabled={disabled}
          minItems={field.minItems}
          onChange={field.onChange}
          options={field.options}
          testId={testId}
          value={field.value}
        />
      );
    case 'multiSelect':
      // Only styling.time_range.options lands here: four unordered entries, small enough that
      // checkboxes beat a picker popover.
      return (
        <DashboardChipList>
          {field.options.map(option => {
            const isSelected = field.value.includes(option.value);

            return (
              <Checkbox
                key={option.value}
                checked={isSelected}
                data-testid={testId ? `${testId}-${option.value}` : undefined}
                disabled={disabled}
                label={option.label}
                onChange={() =>
                  field.onChange(
                    isSelected
                      ? field.value.filter(entry => entry !== option.value)
                      : [...field.value, option.value]
                  )
                }
              />
            );
          })}
        </DashboardChipList>
      );
    case 'colorList':
      return (
        <DashboardColorListField
          disabled={disabled}
          onChange={field.onChange}
          testId={testId}
          value={field.value}
        />
      );
    case 'stringList':
      return (
        <DashboardStringListField
          disabled={disabled}
          maxItems={field.maxItems}
          onChange={field.onChange}
          placeholder={field.placeholder}
          testId={testId}
          value={field.value}
        />
      );
    case 'sortKeys':
      return (
        <DashboardSortKeysField
          disabled={disabled}
          onChange={field.onChange}
          options={field.options}
          testId={testId}
          value={field.value}
        />
      );
    case 'filters':
      return (
        <DashboardFiltersField
          disabled={disabled}
          onChange={field.onChange}
          options={field.options}
          testId={testId}
          value={field.value}
        />
      );
    case 'readOnly':
      return <DashboardEmptyHint data-testid={testId}>{field.value}</DashboardEmptyHint>;
    default: {
      // Exhaustive by construction: a new descriptor kind stops this compiling until it renders.
      const unhandled: never = field;

      return unhandled;
    }
  }
};

export default DashboardFieldControl;
